import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import type { DeepCamelCase } from '@/types/utils'
import { requiredString } from '@/utils/zod'

const attendanceStatus = z.union([z.literal('absent_unexcused'), z.literal('')])

const courseSchema = z.object({
  id: requiredString(),
  display_name: requiredString()
})

const lessonSchema = z.object({
  id: requiredString(),
  date: z.coerce.date(),
  start_time: z.string(),
  end_time: z.string(),
  course: courseSchema
})

const attendanceSchema = z.object({
  id: requiredString(),
  presence: attendanceStatus,
  lesson: lessonSchema
})

const responseSchema = z.array(attendanceSchema)

type APIResponse = z.infer<typeof responseSchema>
type ResponseItem = z.infer<typeof attendanceSchema>
export type StudentAttendance = DeepCamelCase<ResponseItem>

const parseResponse = (response: APIResponse): StudentAttendance[] =>
  response.map(item => ({
    id: item.id,
    presence: item.presence,
    lesson: {
      id: item.lesson.id,
      date: item.lesson.date,
      startTime: item.lesson.start_time,
      endTime: item.lesson.end_time,
      course: {
        id: item.lesson.course.id,
        displayName: item.lesson.course.display_name
      }
    }
  }))

type Params = {
  studentId?: string[]
  dateBefore?: string
  dateAfter?: string
  page?: number
}

const useStudentAttendance = (params: Params = {}, enabled = true) =>
  useQuery({
    queryKey: ['panelPlanAbsencesList', params],
    staleTime: 60 * 1000,
    queryFn: async () => {
      const response = await httpService.panel.panelAttendancesList({
        student_id: params.studentId,
        date_before: params.dateBefore,
        date_after: params.dateAfter,
        page: params.page,
        page_size: DEFAULT_PAGE_SIZE,
        // An empty string represents attendance that hasn't been checked yet
        presence: ['absent_unexcused', ''],
        ordering: 'date,start_time',
        fetchKeys: {
          id: true,
          presence: true,
          lesson: true
        }
      })

      const parsed = parseResponse(responseSchema.parse(response.results))
      return {
        list: parsed,
        count: response.count
      }
    },
    enabled
  })

export default useStudentAttendance
